import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  DataTable,
  Disclose,
  EmptyState,
  Loading,
  Refusal,
  Tooltip,
} from "@lopie/kernel-ui";

import { wantsSandbox } from "./sandbox.js";
import {
  callResult,
  callWhat,
  callsView,
  egressView,
  failed,
  failureKind,
  loaded,
  qaView,
  screenshotAnswer,
  screenshotFailure,
  screenshotRoute,
  unusedLine,
} from "./sandboxReview.js";

// What an approver reads off a request's sandbox (LO-185): the QA report, the
// kernel call log and the egress log, on the request they belong to.
//
// None of it is a verdict this screen reaches. The report's verdict is the
// kernel's (`sandbox_qa.verdict`), the unused grants are the kernel's
// (`sandbox_calls.unused_grants`), the egress comparison is the gateway's rows
// read against the request, and every sentence about what a log does not prove
// is the kernel's own. The screen orders them, puts a word beside every colour,
// and attaches each qualification to the figure it qualifies.

const WRAPS = { "& code": { overflowWrap: "anywhere" } };

/** The same size and weight as the card's own section labels. */
export function Heading({ children, sx }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block", ...sx }}>
      {children}
    </Typography>
  );
}

/**
 * A state as a word first and a colour second: never colour alone.
 *
 * The colour is on the border and the text stays the reading colour. The
 * semantic colours are a hue to be told apart by, not a text colour: the
 * attention amber is 3.9 to 1 on white, which is under what small text needs,
 * and the word is what carries the state either way.
 */
export function Word({ tone, children, title }) {
  const colour = tone ? `kernel.${tone}` : undefined;
  const chip = (
    <Chip
      size="small"
      variant="outlined"
      label={children}
      sx={{
        fontWeight: 600,
        maxWidth: "100%",
        height: "auto",
        color: "text.primary",
        borderColor: colour,
        "& .MuiChip-label": { whiteSpace: "normal", overflowWrap: "anywhere", py: 0.25 },
      }}
    />
  );
  return title ? <Tooltip title={title}>{chip}</Tooltip> : chip;
}

/** The kernel's own sentences about what a log or a report does not show. */
export function Lines({ lines }) {
  if (!lines?.length) return null;
  return (
    <Stack spacing={0.5} component="ul" sx={{ m: 0, pl: 2.5 }}>
      {lines.map((line) => (
        <Typography key={line} variant="body2" component="li" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          {line}
        </Typography>
      ))}
    </Stack>
  );
}

/**
 * A read that did not come back, in the sentence it deserves.
 *
 * A request with no sandbox is information and says what the kernel said. A
 * refusal is the kit's `Refusal`, naming whoever was refused. Anything else is
 * a network or a bug and offers a retry.
 */
function PartFailure({ what, error, onRetry }) {
  if (failureKind(error) === "closed") {
    return (
      <Alert severity="info" variant="outlined">
        <AlertTitle>There is no sandbox to read the {what} from</AlertTitle>
        {error.body?.detail || error.message} <code>{error.error}</code>
      </Alert>
    );
  }
  return (
    <Refusal
      failure={error}
      actor="This console"
      errorTitle={`The ${what} could not be read`}
      onRetry={onRetry}
    />
  );
}

// -- reading the three logs -----------------------------------------------------------

const CALLS_PAGE = 100;
const POLL_MS = 8000;

const READS = {
  qa: (id) => [`/sandboxes/${id}/qa`],
  calls: (id) => [`/sandboxes/${id}/calls`, { limit: CALLS_PAGE }],
  egress: (id) => [`/sandboxes/${id}/egress`],
};

/**
 * The QA report, the call log and the egress log of every pending request that
 * has a sandbox, by request id.
 *
 * Each of the three is read on its own and kept as `{status, body | error}`, so
 * one that fails says so in its own place and does not take the others with it.
 * `undefined` is "not asked yet", which the components render as loading and
 * never as nothing. A run that is still going is read again every few seconds
 * until it is not: an approver watching a run should not have to refresh to see
 * it finish, and a screen that stopped updating would read as a run that had.
 */
export function useSandboxEvidence(get, requests) {
  const [byId, setById] = useState({});
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const ids = useMemo(
    () => (requests || []).filter(wantsSandbox).map((r) => r.request_id),
    [requests],
  );
  const key = ids.join(",");

  const read = useCallback((id, which = Object.keys(READS)) => {
    if (!get) return;
    for (const part of which) {
      const [path, params] = READS[part](encodeURIComponent(id));
      get(path, params).then(
        (body) => alive.current
          && setById((m) => ({ ...m, [id]: { ...m[id], [part]: loaded(body) } })),
        (error) => alive.current
          && setById((m) => ({ ...m, [id]: { ...m[id], [part]: failed(error) } })),
      );
    }
  }, [get]);

  useEffect(() => {
    for (const id of key ? key.split(",") : []) read(id);
  }, [key, read]);

  const running = ids.filter((id) => byId[id]?.qa?.body?.run?.status === "running");
  const runningKey = running.join(",");
  useEffect(() => {
    if (!runningKey) return undefined;
    const timer = setTimeout(() => {
      for (const id of runningKey.split(",")) read(id);
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [runningKey, byId, read]);

  return { evidence: byId, reload: read };
}

// -- a screenshot ---------------------------------------------------------------------

/**
 * One screenshot the report names.
 *
 * The report holds a path, not an address, and the address comes from the
 * kernel on demand. It is good for about five minutes and is only ever an
 * `<img src>`: the bucket has no CORS, so it cannot be fetched. So it is asked
 * for late, when the screenshot is about to be seen (near the viewport for a
 * failing check, which is the one the reader came for, and on a click for the
 * gallery), so the five minutes start when they are needed. An image that does
 * not load is asked for once more, because an expired address looks exactly
 * like a missing file; after that it says why and offers another try. It is
 * never an empty box.
 */
const LIVE_ROUTE = (requestId, runId, path) => [screenshotRoute(requestId, path), { run_id: runId }];

export function Screenshot({
  requestId, runId, path, label, get, eager = false,
  // A decision's kept copy (LO-199) is signed by another route and refused in
  // other words; the rest of this is the same.
  routeOf = LIVE_ROUTE, failureOf = screenshotFailure,
}) {
  const [state, setState] = useState({ status: "idle" });
  const alive = useRef(true);
  const place = useRef(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async (again = false) => {
    setState({ status: "loading", again });
    try {
      const [route, params] = routeOf(requestId, runId, path);
      const answer = screenshotAnswer(await get(route, params));
      if (alive.current) setState({ status: "ready", url: answer.url, again });
    } catch (e) {
      if (alive.current) setState({ status: "failed", reason: failureOf(e) });
    }
  }, [get, requestId, runId, path, routeOf, failureOf]);

  useEffect(() => {
    if (!eager) return undefined;
    const node = place.current;
    // No observer (an old browser, a test harness) means ask at once, which is
    // the safe direction: a picture that loads early beats one that never does.
    if (!node || typeof IntersectionObserver === "undefined") {
      load();
      return undefined;
    }
    const watch = new IntersectionObserver((seen) => {
      if (seen.some((entry) => entry.isIntersecting)) {
        watch.disconnect();
        load();
      }
    }, { rootMargin: "300px" });
    watch.observe(node);
    return () => watch.disconnect();
  }, [eager, load]);

  const name = label ? `${label} (${path})` : path;
  let body;
  if (state.status === "idle") {
    body = eager ? (
      <Typography variant="caption" color="text.secondary">
        Screenshot: {name}. It is fetched when it comes into view.
      </Typography>
    ) : (
      <Button size="small" variant="outlined" onClick={() => load()} sx={{ textAlign: "left" }}>
        Show screenshot: {label || path}
      </Button>
    );
  } else if (state.status === "loading") {
    body = <Loading label={`Loading the screenshot ${name}`} minHeight={60} size={20} />;
  } else if (state.status === "failed") {
    body = (
      <Alert
        severity="warning"
        variant="outlined"
        action={<Button size="small" color="inherit" onClick={() => load()}>Try again</Button>}
      >
        <AlertTitle>The screenshot could not be loaded</AlertTitle>
        {state.reason} The report names it as <code>{path}</code>.
      </Alert>
    );
  } else {
    body = (
      <Link
        href={state.url}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open the screenshot ${name} at full size`}
        sx={{ display: "block", maxWidth: "100%" }}
      >
        <Box
          component="img"
          src={state.url}
          alt={`Screenshot: ${name}`}
          onError={() => {
            if (!state.again) {
              load(true);
              return;
            }
            setState({
              status: "failed",
              reason: "The address it was given did not load, even when asked for twice. It lasts about five minutes and the file may be gone.",
            });
          }}
          sx={{
            display: "block", maxWidth: "100%", height: "auto",
            border: 1, borderColor: "divider", borderRadius: 1,
          }}
        />
      </Link>
    );
  }
  return <Box ref={place} sx={{ minWidth: 0, maxWidth: "100%" }}>{body}</Box>;
}

// -- the QA report --------------------------------------------------------------------

const CHECK_WORD = { fail: "failed", skipped: "skipped", pass: "passed" };

/**
 * One failing check: what failed, where, how to see it for yourself, and what
 * the page looked like. The steps come first because they are what turns "the
 * agent says so" into something a person can check in a minute.
 */
function Finding({ check, requestId, runId, get }) {
  const evidence = Array.isArray(check.evidence?.findings) ? check.evidence.findings : [];
  const steps = Array.isArray(check.reproduction) ? check.reproduction : [];
  // A failure the kernel marked inconclusive (LO-193) could not be judged: nothing
  // the app did failed. It is worded as that, and is still not a pass.
  const unjudged = check.inconclusive === true;
  return (
    <Paper
      variant="outlined"
      role="group"
      aria-label={`${unjudged ? "inconclusive" : "failed"} check: ${check.title}`}
      sx={{ p: 1.5, minWidth: 0, ...WRAPS }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          {unjudged ? (
            <Word tone="attention" title="The check could not be judged, so nothing the app did failed. It is not a pass.">
              inconclusive · not a finding about the app
            </Word>
          ) : (
            <Word tone="refused">failed</Word>
          )}
          <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere", minWidth: 0 }}>
            {check.title}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={check.persona ? `as ${check.persona}` : "whole run"}
          />
        </Stack>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{check.detail}</Typography>
        {check.route ? (
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            First seen at <code>{check.route}</code>
          </Typography>
        ) : null}
        <Box>
          <Heading>To reproduce</Heading>
          {steps.length ? (
            <Box component="ol" sx={{ m: 0, pl: 3 }}>
              {steps.map((step, i) => (
                <Typography key={i} variant="body2" component="li" sx={{ overflowWrap: "anywhere" }}>
                  {step}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              The report gives no steps for this check.
            </Typography>
          )}
        </Box>
        {check.screenshot ? (
          <Screenshot requestId={requestId} runId={runId} path={check.screenshot} label={check.title} get={get} eager />
        ) : null}
        {evidence.length ? (
          <Disclose summary="What the check saw." label="Evidence" count={evidence.length}>
            <Lines lines={evidence.map((item) => (typeof item === "string" ? item : JSON.stringify(item).slice(0, 300)))} />
          </Disclose>
        ) : null}
      </Stack>
    </Paper>
  );
}

/** Checks that did not fail, as words: a skipped one is not a pass and says why. */
function OtherChecks({ skipped, passing }) {
  return (
    <Stack spacing={1}>
      {skipped.length ? (
        <Box>
          <Heading>Could not be made ({skipped.length})</Heading>
          <Stack spacing={0.5} component="ul" sx={{ m: 0, pl: 2.5 }}>
            {skipped.map((c) => (
              <Typography key={`${c.persona}:${c.id}`} variant="body2" component="li" sx={{ overflowWrap: "anywhere" }}>
                <strong>{c.title}</strong>: skipped. {c.detail} Not counted as a pass.
              </Typography>
            ))}
          </Stack>
        </Box>
      ) : null}
      {passing.length ? (
        <Disclose
          summary={`${passing.length} ${passing.length === 1 ? "check" : "checks"} found nothing.`}
          label="Which"
          count={passing.length}
        >
          <Stack spacing={0.25} component="ul" sx={{ m: 0, pl: 2.5 }}>
            {passing.map((c) => (
              <Typography key={`${c.persona}:${c.id}`} variant="body2" component="li" sx={{ overflowWrap: "anywhere" }}>
                <strong>{c.title}</strong>: {CHECK_WORD.pass}
              </Typography>
            ))}
          </Stack>
        </Disclose>
      ) : null}
    </Stack>
  );
}

const PERSONA_STATUS = {
  failed: { tone: "refused", word: "failed" },
  error: { tone: "attention", word: "did not complete" },
  skipped: { tone: "stale", word: "skipped" },
  passed: { tone: "permitted", word: "passed on synthetic data" },
};

/** One persona's part of the report: failures first, the rest one click in. */
function PersonaReport({ persona, defined, requestId, runId, get }) {
  const status = PERSONA_STATUS[persona.status] || PERSONA_STATUS.skipped;
  const tags = defined?.tags || defined?.groups || [];
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label={`QA report as ${persona.name}`}
      sx={{ p: 1.5, minWidth: 0, ...WRAPS }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="subtitle2" sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
            {persona.name}
          </Typography>
          <Word tone={status.tone}>{status.word}</Word>
          {defined ? (
            tags.length ? (
              tags.map((t) => <Chip key={t} size="small" variant="outlined" label={t} />)
            ) : (
              <Typography variant="caption" color="text.secondary">
                holds no tags
              </Typography>
            )
          ) : null}
          <Typography variant="caption" color="text.secondary">
            {persona.failing.length} failed · {persona.skipped.length} skipped · {persona.passing.length} passed
            {persona.total ? ` of ${persona.total} checks` : ""}
          </Typography>
        </Stack>

        {persona.accessLine ? (
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{persona.accessLine}</Typography>
        ) : null}

        {persona.failing.map((check) => (
          <Finding key={check.id} check={{ ...check, persona: persona.name }} requestId={requestId} runId={runId} get={get} />
        ))}

        <OtherChecks skipped={persona.skipped} passing={persona.passing} />

        {persona.refusals.length ? (
          <Disclose
            summary="The refusals this persona met, with what the kernel called each."
            label="Refusals"
            count={persona.refusals.length}
          >
            <Lines
              lines={persona.refusals.map((r) =>
                `${r.call}: ${r.status || ""} ${r.error || ""}${r.screen ? ` (on ${r.screen})` : ""}`.replace(/\s+/g, " "))}
            />
          </Disclose>
        ) : null}
        {persona.writes.length ? (
          <Disclose
            summary="The writes this persona made while it was explored."
            label="Writes"
            count={persona.writes.length}
          >
            <Lines
              lines={persona.writes.map((w) =>
                `${w.verb || w.route}: ${w.status || ""}${w.error ? ` ${w.error}` : ""}${w.screen ? ` (on ${w.screen})` : ""}`.replace(/\s+/g, " "))}
            />
          </Disclose>
        ) : null}
        {persona.screenshots.length ? (
          <Disclose
            summary="What each screen looked like to this persona."
            label="Screenshots"
            count={persona.screenshots.length}
          >
            <Stack spacing={1.5}>
              {persona.screenshots.map((s) => (
                <Screenshot key={s.path} requestId={requestId} runId={runId} path={s.path} label={s.label} get={get} />
              ))}
            </Stack>
          </Disclose>
        ) : null}
        {persona.truncated ? (
          <Typography variant="caption" color="text.secondary">
            The crawl stopped at a limit: {persona.truncated}. A screen it did not reach was not checked.
          </Typography>
        ) : null}
        {persona.notes.length ? <Lines lines={persona.notes} /> : null}
      </Stack>
    </Paper>
  );
}

/**
 * The verdict, as a word that carries its own qualification.
 *
 * A pass reads "passed against synthetic data and these personas" in the chip
 * itself, the half of the label that carries the claim, rather than "passed"
 * with a caveat somewhere below. Everything that is not a pass says which
 * thing it is: a finding about the app, a run that did not complete (which is
 * the platform's and not the app's), a run still going, or a run made for a
 * ceiling that is not the one being asked for.
 */
function verdictOf(view) {
  const failedCount = view.totals?.failed ?? 0;
  switch (view.kind) {
    case "passed":
      return {
        tone: "permitted", word: view.label,
        title: "Every persona passed every check that could be made, for this build and this manifest. It is not a safety claim.",
      };
    case "failed": {
      const unjudged = view.totals?.inconclusive ?? 0;
      // Every failure is one that could not be judged: nothing the app did failed,
      // and the run is still not a pass (LO-193).
      if (view.inconclusiveOnly) {
        return {
          tone: "attention",
          word: `inconclusive · ${unjudged} ${unjudged === 1 ? "check" : "checks"} could not be judged`,
          title: "Nothing the app did failed: the egress gateway was not confirmed working, so the log could not say. That is the platform's, and it is not a pass.",
        };
      }
      return {
        tone: "refused",
        word: `failed · ${failedCount} ${failedCount === 1 ? "check" : "checks"} failed${unjudged ? ` (${unjudged} inconclusive)` : ""}`,
        title: "A reproducible finding about the app, not the platform.",
      };
    }
    case "running":
      return { tone: "attention", word: "running · not finished", title: "No verdict yet." };
    case "error":
      return {
        tone: "attention", word: "did not complete",
        title: "The run did not finish. That is the platform's, and it is neither a finding about the app nor a pass.",
      };
    case "earlier":
      return {
        tone: "attention", word: "passed for an earlier manifest · not this one",
        title: "A run passed, but for a ceiling that is not the one being asked for now. It says nothing about this one.",
      };
    default:
      return { tone: "stale", word: "not run", title: "Nothing has been run against this sandbox." };
  }
}

/** The QA report for one request's sandbox, on the request. */
export function QaReport({ request, part, get, reload }) {
  const requestId = request.request_id;
  const heading = <Heading>QA report</Heading>;
  if (!part || part.status === "loading") {
    return <>{heading}<Loading label="Reading the QA report" minHeight={80} /></>;
  }
  if (part.status === "failed") {
    return (
      <>
        {heading}
        <PartFailure what="QA report" error={part.error} onRetry={() => reload(requestId, ["qa"])} />
      </>
    );
  }

  const view = qaView(part.body);
  if (view.kind === "none") {
    return (
      <>
        {heading}
        <Stack spacing={0.75} sx={{ alignItems: "flex-start" }}>
          <Word tone="stale">{verdictOf(view).word}</Word>
          <Typography variant="body2">
            No QA run has been made against this sandbox yet, so there is nothing to read.
            {view.reason ? <> {view.reason[0].toUpperCase() + view.reason.slice(1)}.</> : null}
          </Typography>
          <Button size="small" variant="outlined" onClick={() => reload(requestId, ["qa", "calls", "egress"])}>
            Check again
          </Button>
        </Stack>
      </>
    );
  }

  const verdict = verdictOf(view);
  const { run, totals } = view;
  const defined = new Map((request.proposed?.personas || []).map((p) => [p.name, p]));
  const checked = totals.checks
    ? `${totals.passed} of ${totals.checks} checks found nothing, ${totals.failed} failed, ${totals.skipped} skipped`
    : "";

  return (
    <>
      {heading}
      <Stack spacing={1.25} sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Word tone={verdict.tone} title={verdict.title}>{verdict.word}</Word>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            Run <code>{run.run_id}</code>
            {run.started_at ? ` · started ${run.started_at}` : ""}
            {run.build_id ? <> · build <code>{run.build_id}</code></> : null}
          </Typography>
          <Button size="small" onClick={() => reload(requestId, ["qa", "calls", "egress"])}>
            Refresh
          </Button>
        </Stack>

        {totals.personas || checked ? (
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            {totals.personas
              ? `${totals.personasPassed} of ${totals.personas} ${totals.personas === 1 ? "persona" : "personas"} passed. `
              : ""}
            {checked}
            {totals.skipped ? ". A skipped check could not be made and is not counted as a pass" : ""}
            {checked || totals.personas ? "." : ""}
          </Typography>
        ) : null}

        {!view.bound ? (
          <Alert severity="warning" variant="outlined">
            This run was made for an earlier manifest than the request has now, so it is a run
            for a ceiling nobody is asking for.
            {view.gate?.reason ? <> {view.gate.reason[0].toUpperCase() + view.gate.reason.slice(1)}.</> : null}
          </Alert>
        ) : null}

        {view.error ? (
          <Alert severity="warning" variant="outlined">
            <AlertTitle>The run did not complete: {view.error.reason}</AlertTitle>
            {view.error.detail} This is the platform&apos;s, not a finding about the app, and it is not a pass.
          </Alert>
        ) : null}
        {view.liveSkipped ? (
          <Alert severity="warning" variant="outlined">
            <AlertTitle>The live run did not happen: {view.liveSkipped.reason || "no reason given"}</AlertTitle>
            {view.liveSkipped.detail} Nothing was explored as any persona, which is why this is not a pass.
          </Alert>
        ) : null}

        {view.findings.length ? (
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            <strong>Failing:</strong>{" "}
            {view.findings.map((f) => `${f.title} (${f.persona ? `as ${f.persona}` : "whole run"}${f.inconclusive === true ? ", inconclusive" : ""})`).join("; ")}.
          </Typography>
        ) : null}

        {view.doesNotProve.length ? (
          <Disclose
            summary="What this report does not cover."
            label="Read it"
            count={view.doesNotProve.length}
          >
            <Lines lines={view.doesNotProve} />
          </Disclose>
        ) : null}

        {view.runWide.failing.length || view.runWide.skipped.length || view.runWide.passing.length ? (
          <Stack spacing={1.25}>
            <Heading>The whole run</Heading>
            {view.runWide.failing.map((check) => (
              <Finding key={check.id} check={check} requestId={requestId} runId={run.run_id} get={get} />
            ))}
            <OtherChecks skipped={view.runWide.skipped} passing={view.runWide.passing} />
          </Stack>
        ) : null}

        {view.personas.length ? (
          <Stack spacing={1.25}>
            <Heading>
              As each persona ({view.personas.length})
            </Heading>
            {view.personas.map((p) => (
              <PersonaReport
                key={p.name}
                persona={p}
                defined={defined.get(p.name)}
                requestId={requestId}
                runId={run.run_id}
                get={get}
              />
            ))}
          </Stack>
        ) : null}
        {view.truncated ? (
          <Typography variant="caption" color="text.secondary">
            This report was cut to fit what is stored, and the bulkiest evidence was dropped first.
          </Typography>
        ) : null}
      </Stack>
    </>
  );
}

// -- the call log ---------------------------------------------------------------------

const RESULT_TONE = { allowed: "permitted", empty: "stale", refused: "refused" };
const RESULT_GLOSS = {
  allowed: "The kernel answered and returned or wrote something.",
  empty: "The kernel answered and there was nothing to return. This is not a refusal: the person cannot see anything, or nothing was seeded.",
  refused: "The kernel refused. The code is its own.",
};

function ResultChip({ call }) {
  const r = callResult(call);
  const bits = [r.word];
  if (r.code) bits.push(r.code);
  if (r.status) bits.push(String(r.status));
  if (r.kind === "allowed" && call.count) bits.push(`${call.count} ${call.count === 1 ? "record" : "records"}`);
  return <Word tone={RESULT_TONE[r.kind]} title={RESULT_GLOSS[r.kind]}>{bits.join(" · ")}</Word>;
}

// Each column keeps a width of its own, so a narrow screen scrolls the table in
// its container and does not squeeze a name down to one letter a line.
const CALL_COLUMNS = [
  {
    key: "persona", label: "Persona", sx: { minWidth: 150 },
    render: (c) => <Box component="code" sx={{ overflowWrap: "anywhere" }}>{c.persona}</Box>,
  },
  {
    key: "call", label: "Call", sx: { minWidth: 170 },
    render: (c) => <Box component="code" sx={{ overflowWrap: "anywhere" }}>{callWhat(c)}</Box>,
  },
  { key: "type", label: "Type", sx: { minWidth: 100 }, render: (c) => c.type || "none" },
  { key: "result", label: "Result", sx: { minWidth: 190 }, render: (c) => <ResultChip call={c} /> },
  {
    key: "detail", label: "What the kernel said", sx: { minWidth: 220 },
    render: (c) => (
      <Box sx={{ overflowWrap: "anywhere" }}>
        {c.detail || (c.tags?.length ? `tags: ${c.tags.join(", ")}` : "none")}
        {c.detail && c.tags?.length ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            tags: {c.tags.join(", ")}
          </Typography>
        ) : null}
      </Box>
    ),
  },
];

/**
 * The kernel's record of what the sandbox's client asked, and was told.
 *
 * The headline figures are over every recorded call and do not move with the
 * filter; the kernel computes them that way and the label says so. A refusal is
 * named by its code in the headline, because "3 refused" and "refused:
 * not_in_manifest" are different amounts of information. The unused grants sit
 * under it, and are the same lines the approve dialog flags beside the checkbox
 * that strikes them.
 */
export function CallLog({ request, part, get, reload }) {
  const requestId = request.request_id;
  const [persona, setPersona] = useState("");
  const [shown, setShown] = useState(null); // {persona, rows, cursor, hasMore} once filtered or paged
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  const base = part?.status === "ok" ? part.body : null;

  // A fresh read of the log replaces what was filtered from the last one.
  useEffect(() => {
    setShown(null);
    setFailure(null);
  }, [part]);

  const fetchPage = useCallback(async (who, cursor, rows) => {
    setBusy(true);
    setFailure(null);
    try {
      const body = await get(`/sandboxes/${encodeURIComponent(requestId)}/calls`, {
        persona: who, limit: CALLS_PAGE, cursor,
      });
      setShown({
        persona: who,
        rows: [...rows, ...(body.calls || [])],
        cursor: body.cursor || "",
        hasMore: Boolean(body.has_more),
      });
    } catch (e) {
      setFailure(e);
    } finally {
      setBusy(false);
    }
  }, [get, requestId]);

  const choose = (who) => {
    setPersona(who);
    setShown(null);
    if (who) fetchPage(who, "", []);
  };
  useEffect(() => {
    // The log was read again while a persona was chosen: read their calls again.
    if (persona && part?.status === "ok") fetchPage(persona, "", []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [part]);

  const heading = <Heading>What the sandbox asked the kernel</Heading>;
  if (!part || part.status === "loading") {
    return <>{heading}<Loading label="Reading the call log" minHeight={80} /></>;
  }
  if (part.status === "failed") {
    return (
      <>
        {heading}
        <PartFailure what="call log" error={part.error} onRetry={() => reload(requestId, ["calls"])} />
      </>
    );
  }

  const view = callsView(base);
  if (view.none) {
    return (
      <>
        {heading}
        <Alert severity="info" variant="outlined">
          <AlertTitle>No call has been recorded: this is not a clean log</AlertTitle>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            The sandbox has no recorded call, so no requested grant is flagged as unused and nothing
            here says the app is quiet. The kernel&apos;s own wording:
          </Typography>
          <Lines lines={view.doesNotProve} />
        </Alert>
      </>
    );
  }

  const rows = shown ? shown.rows : persona ? [] : base.calls || [];
  const hasMore = shown ? shown.hasMore : persona ? false : Boolean(base.has_more);
  const cursor = shown ? shown.cursor : base.cursor || "";
  const refusals = Object.entries(base.summary?.by_outcome || {})
    .filter(([code]) => !["allowed", "empty"].includes(code));
  const unused = base.unused_grants || [];

  return (
    <>
      {heading}
      <Stack spacing={1.25} sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          <strong>{view.total} {view.total === 1 ? "call" : "calls"} recorded</strong> across{" "}
          {view.personas.length} {view.personas.length === 1 ? "persona" : "personas"}:{" "}
          {view.allowed} allowed, {view.empty} empty (answered, nothing to return), {view.refused} refused
          {refusals.length ? <> ({refusals.map(([code, n]) => `${code} ×${n}`).join(", ")})</> : null}.
          {view.truncated ? " The log is longer than one report reads, so these figures are over its first part." : ""}
        </Typography>

        <Box>
          <Heading>
            {unused.length
              ? `Asked for and never used in this sandbox (${unused.length})`
              : "Asked for and never used"}
          </Heading>
          {unused.length ? (
            <Stack spacing={0.75}>
              <Stack spacing={0.5} component="ul" sx={{ m: 0, pl: 2.5 }} aria-label="requested grants no call used">
                {unused.map((g) => (
                  <Typography
                    key={`${g.kind}:${g.type || ""}:${g.verb || ""}:${g.name || ""}`}
                    variant="body2"
                    component="li"
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    <Word tone="attention" title={g.detail}>narrowing candidate</Word>{" "}
                    <code>{unusedLine(g)}</code>
                  </Typography>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                A reason to ask why the app wants it, and not a finding: no recorded call named it, as
                the personas this run used. Approving opens the same lines beside the boxes that strike them.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Every grant the request asks for was named by at least one recorded call. That is by type or verb
              and not by tag, and only in what this run did.
            </Typography>
          )}
        </Box>

        <Disclose
          align="row"
          summary="Each call, in the order it was made."
          label="Show the calls"
          count={view.total}
        >
          <Stack spacing={1.25} sx={{ minWidth: 0 }}>
            <TextField
              select
              size="small"
              label="Show calls by"
              value={persona}
              onChange={(e) => choose(e.target.value)}
              sx={{ maxWidth: "26rem" }}
              slotProps={{ select: { native: true } }}
              helperText="The figures above are over every call and do not change with this."
            >
              <option value="">All personas ({view.total})</option>
              {view.personas.map((p) => (
                <option key={p.name} value={p.name}>{p.name} ({p.total})</option>
              ))}
            </TextField>
            {failure ? <PartFailure what="calls" error={failure} onRetry={() => choose(persona)} /> : null}
            {busy && !rows.length ? <Loading label="Reading the calls" minHeight={60} size={20} /> : null}
            {!busy || rows.length ? (
              <DataTable
                aria-label="calls the sandbox made"
                columns={CALL_COLUMNS}
                rows={rows}
                getRowKey={(c) => c.call_id}
                empty={(
                  <EmptyState title={`No call recorded as ${persona || "anyone"}`}>
                    <Typography variant="body2">
                      The log has no call by this persona. That is not a refusal. It may mean the run never
                      signed in as them, and the log cannot say which.
                    </Typography>
                  </EmptyState>
                )}
              />
            ) : null}
            {hasMore ? (
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                sx={{ alignSelf: "flex-start" }}
                onClick={() => fetchPage(persona, cursor, shown ? shown.rows : base.calls || [])}
              >
                {busy ? "Reading…" : "Show more calls"}
              </Button>
            ) : null}
          </Stack>
        </Disclose>

        {view.doesNotProve.length ? (
          <Disclose summary="This log is best-effort." label="What it does not show" count={view.doesNotProve.length}>
            <Lines lines={view.doesNotProve} />
          </Disclose>
        ) : null}
      </Stack>
    </>
  );
}

// -- egress: declared against observed -----------------------------------------------

const EGRESS_VERDICT = {
  undeclared: { tone: "refused", word: "failure · contacted, never declared" },
  declared: { tone: "permitted", word: "declared · contacted" },
  never: { tone: "attention", word: "declared · never contacted · narrowing candidate" },
  unobserved: { tone: "stale", word: "declared · no attempt recorded" },
  // The gateway that fills the log was not working (LO-193): whether it was tried
  // is not known, which is not the same as never tried and is never a candidate.
  unrecorded: { tone: "attention", word: "declared · not recorded · the gateway was not working" },
};

const EGRESS_COLUMNS = [
  {
    key: "host", label: "Host", sx: { minWidth: 190 },
    render: (r) => <Box component="code" sx={{ overflowWrap: "anywhere" }}>{r.host}</Box>,
  },
  {
    key: "verdict", label: "Declared against observed", sx: { minWidth: 230 },
    render: (r) => {
      const v = EGRESS_VERDICT[r.verdict];
      return (
        <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
          <Word tone={v.tone}>{v.word}</Word>
          {r.verdict === "undeclared" ? (
            <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
              {r.letThrough
                ? `The gateway let ${r.letThrough} ${r.letThrough === 1 ? "attempt" : "attempts"} through.`
                : "The gateway blocked it."}
              {r.blocked && r.letThrough ? ` It blocked ${r.blocked}.` : ""}
            </Typography>
          ) : null}
          {r.verdict === "declared" && r.blocked ? (
            <Typography variant="caption">
              The gateway blocked {r.blocked} {r.blocked === 1 ? "attempt" : "attempts"} of it.
            </Typography>
          ) : null}
        </Stack>
      );
    },
  },
  {
    key: "attempts", label: "Attempts", align: "right",
    render: (r) => (r.attempts ? r.attempts : "none recorded"),
  },
];

/**
 * Whether the gateway that fills the egress log was working (LO-193): a word and the
 * kernel's own sentence about why, and its last failed write when it has one. What a
 * working gateway does not prove is on that word, where it is read, and is not said
 * of one that is not working, which has its own, bigger, sentence.
 */
function GatewayLine({ gateway }) {
  return (
    <Stack spacing={0.5} component="section" aria-label="Egress gateway">
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="caption" color="text.secondary">Egress gateway</Typography>
        <Word tone={gateway.tone}>{gateway.word}</Word>
      </Stack>
      {gateway.detail ? (
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          {gateway.detail}
        </Typography>
      ) : null}
      {gateway.lastError ? (
        <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
          Its last failed write{gateway.lastErrorAt ? `, ${gateway.lastErrorAt}` : ""}: <code>{gateway.lastError}</code>
        </Typography>
      ) : null}
      {gateway.healthy ? (
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          Working means it reported in the last few minutes and its writes landed. It does not mean every
          attempt in a run was seen.
        </Typography>
      ) : null}
    </Stack>
  );
}

/**
 * What the sandbox's function tried to reach, set against what was declared.
 *
 * A host contacted and never declared is a failure, whatever the gateway did
 * with it. A host declared and never contacted is a narrowing candidate and
 * not a finding. An empty log is neither: it is the absence of a reading, and
 * the kernel's own sentences about why are shown at the top of it rather than
 * left as a clean list.
 */
export function ObservedEgress({ part, declared, hasSandbox, requestId, reload }) {
  if (!hasSandbox) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not recorded: this request has no sandbox, so nothing here shows which hosts the app called, and the
        list beside this is what it asks for and not what it does.
      </Typography>
    );
  }
  if (!part || part.status === "loading") {
    return <Loading label="Reading the egress log" minHeight={80} size={24} />;
  }
  if (part.status === "failed") {
    return <PartFailure what="egress log" error={part.error} onRetry={() => reload(requestId, ["egress"])} />;
  }

  const view = egressView(part.body, declared);
  return (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      {view.gateway ? <GatewayLine gateway={view.gateway} /> : null}
      {view.notRecorded ? (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>Not recorded: the egress gateway is not confirmed working</AlertTitle>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {view.readingDetail} No declared host is flagged, and this empty list says nothing about what the
            function did: it is not a clean log, and it is not that nothing was contacted. The kernel&apos;s own wording:
          </Typography>
          <Lines lines={view.doesNotProve} />
        </Alert>
      ) : view.empty && view.reading === "nothing_contacted" ? (
        <Alert severity="info" variant="outlined">
          <AlertTitle>Nothing contacted through the gateway: a reading, not proof</AlertTitle>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {view.readingDetail} No declared host is flagged from it, because one run that called nothing is thin
            evidence that a host is not needed, and code that ignores the proxy settings is stopped by the network
            and leaves no row. The kernel&apos;s own wording:
          </Typography>
          <Lines lines={view.doesNotProve} />
        </Alert>
      ) : view.empty ? (
        <Alert severity="info" variant="outlined">
          <AlertTitle>Nothing observed: this is not a clean log</AlertTitle>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            No attempt to reach any host was recorded, so no declared host is flagged and the hosts listed
            say nothing about what the function does. The kernel did not say whether the gateway is working,
            so this is not read as nothing contacted. The kernel&apos;s own wording:
          </Typography>
          <Lines lines={view.doesNotProve} />
        </Alert>
      ) : (
        <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
          <strong>
            {view.failures.length
              ? `${view.failures.length} ${view.failures.length === 1 ? "host" : "hosts"} contacted and never declared`
              : "No undeclared host was contacted"}
          </strong>
          {view.failures.length ? " (a failure)" : ""}; {view.never.length} declared and never contacted
          {view.never.length ? " (narrowing candidates)" : ""}.
        </Typography>
      )}

      {view.incomplete ? (
        <Alert severity="warning" variant="outlined">
          The gateway is not confirmed working ({view.gateway.word}), so the hosts below may be incomplete: an
          attempt it could not record is not here, and a declared host it shows no attempt at is not known to have
          gone untried.
        </Alert>
      ) : null}

      {view.rows.length ? (
        <DataTable
          aria-label="declared hosts against observed hosts"
          columns={EGRESS_COLUMNS}
          rows={view.rows}
          getRowKey={(r) => r.host}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          The request declares no host and none was contacted.
        </Typography>
      )}

      {view.kernelHosts.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          Also reached, and allowed for every sandbox because they are the kernel&apos;s own, so not findings:{" "}
          {view.kernelHosts.join(", ")}.
        </Typography>
      ) : null}

      {!view.empty && view.doesNotProve.length ? (
        <Disclose summary="A host is only here if the function went through the gateway." label="What it does not show" count={view.doesNotProve.length}>
          <Lines lines={view.doesNotProve} />
        </Disclose>
      ) : null}
    </Stack>
  );
}

// -- narrowing, in the approve dialog -------------------------------------------------

/**
 * The flag beside a box that strikes a line the sandbox did not use.
 *
 * Words first, and the qualification on the flag itself: an unused line is a
 * reason to ask why the app wants it, and a path the run never took looks
 * exactly like a line the app does not need.
 */
export function UnusedMark({ what }) {
  return (
    <Tooltip title="No call or attempt recorded in the sandbox named this. A reason to ask why the app wants it, and not a finding: a path the run never took looks the same.">
      <Chip
        size="small"
        variant="outlined"
        label={what}
        sx={{ color: "text.primary", borderColor: "kernel.attention" }}
      />
    </Tooltip>
  );
}

/**
 * What the sandbox says about the lines below, said once above them, with the
 * one control that strikes them together.
 *
 * Nothing is struck for the approver: the default is every line kept, and a
 * trim you did not make is not made for you. What this adds is the flags and a
 * single button, so striking what went unused is one motion and not one tick
 * per line. A line this dialog has no box for (a kind of grant it does not know)
 * is listed and not offered a box that is not there; a write with no user behind
 * it and an aggregate have one, because the approve route trims both (LO-191). When there
 * is nothing to flag it says why, because a dialog that is silent about it
 * reads as a sandbox that found every line needed.
 */
export function UnusedBlock({ narrow, here, kept, hasSandbox, onStrike }) {
  if (!hasSandbox) return null;

  if (here > 0 || narrow.other.length) {
    return (
      <Alert severity="info" variant="outlined" icon={false}>
        <AlertTitle>
          {here
            ? `${here} requested ${here === 1 ? "line" : "lines"} went unused in the sandbox`
            : "Some requested lines went unused in the sandbox"}
        </AlertTitle>
        <Typography variant="body2">
          {here ? "Each is flagged beside its own box below. " : ""}
          A reason to ask why the app wants them, and not a finding: it rests on the
          calls and hosts the sandbox recorded, as the personas this run used, and a
          path the run never took looks the same. Nothing is struck until you strike it.
        </Typography>
        {here ? (
          <Button
            size="small"
            variant="outlined"
            disabled={!kept}
            onClick={onStrike}
            sx={{ mt: 1 }}
          >
            {kept
              ? `Strike the ${kept} unused ${kept === 1 ? "line" : "lines"}`
              : "All the unused lines are struck"}
          </Button>
        ) : null}
        {narrow.other.length ? (
          <Typography variant="body2" sx={{ mt: 1, overflowWrap: "anywhere" }}>
            Also unused, and this dialog has no box to strike them with:{" "}
            {narrow.other.map(unusedLine).join("; ")}.
          </Typography>
        ) : null}
        {narrow.calls === "empty" || narrow.egress_log === "empty" || narrow.egress_log === "not_recorded" ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {narrow.calls === "empty" ? "No call was recorded, so no read or write is flagged. " : ""}
            {narrow.egress_log === "empty" ? "No egress attempt was recorded, so no host is flagged. " : ""}
            {narrow.egress_log === "not_recorded"
              ? "The egress gateway is not confirmed working, so what the sandbox tried to reach is not recorded and no host is flagged. "
              : ""}
            An empty log is not a clean one.
          </Typography>
        ) : null}
      </Alert>
    );
  }

  if (narrow.basis === "none") {
    const empty = [
      narrow.calls === "empty" ? "call" : null,
      narrow.egress_log === "empty" ? "egress attempt" : null,
    ].filter(Boolean);
    return (
      <Alert severity="info" variant="outlined" icon={false}>
        <Typography variant="body2">
          {empty.length
            ? `Nothing is flagged as unused: the sandbox has recorded no ${empty.join(" and no ")}. `
              + "That says the run has not happened, not that every line is needed."
            : "Nothing is flagged as unused: the sandbox's logs have not been read, "
              + "so this says nothing about which lines are needed."}
          {narrow.egress_log === "not_recorded"
            ? " The egress gateway is not confirmed working, so what the sandbox tried to reach is not "
              + "recorded and no host is flagged."
            : ""}
        </Typography>
      </Alert>
    );
  }
  return null;
}
